import { useSession } from './App';
import { employeeAccessTargets } from './hr-permissions';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccess, useEmployee, useRows } from './api/data';
import { getObject, type Row, type GetPath } from './api/client';
import { ActionForm, type Action } from './forms';
import { DocumentUpload, type EmployeeKind } from './employee-records';
import { Button, Empty, ErrorState, Loading, useToast } from './ui';
import { fieldLabel } from './i18n';
import { findResource } from './modules';
import { actionOperation } from './resource-routes';
import { StructuredData } from './structured-data';

const kinds: EmployeeKind[] = [
  'documents',
  'compensation',
  'payroll',
  'screenings',
  'right-to-work',
];
export function EmployeeActionPage() {
  const location = useRouterState({ select: (state) => state.location });
  const parts = location.pathname.split('/');
  const id = parts[3] || '';
  const operation = parts[4] || '';
  const search = location.search as { kind?: string; versionId?: string };
  const access = useAccess();
  const session = useSession();
  const employee = useEmployee(id);
  const resolution = useQuery({
    queryKey: ['role-resolution', id],
    enabled:
      operation.startsWith('role-profile') &&
      (access.can('work_plans.read') || access.can('work_plans.manage')),
    queryFn: ({ signal }) =>
      getObject<Row>('/api/v1/employees/{id}/role-profile-resolution', { id }, signal),
  });
  const assignments = useRows(
    '/api/v1/employees/{id}/role-profile-assignments',
    {},
    { params: { id }, enabled: operation === 'role-profile-history' },
  );
  const preview = useQuery({
    queryKey: ['role-profile-preview', search.versionId, employee.data?.seniorityId],
    enabled: operation === 'role-profile-preview' && Boolean(search.versionId && employee.data),
    queryFn: ({ signal }) =>
      getObject<Row>(
        '/api/v1/role-profile-versions/{id}/preview',
        { id: search.versionId! },
        signal,
        { seniorityId: employee.data!.seniorityId },
      ),
  });
  const personal = useQuery({
    queryKey: ['personal-details', id],
    enabled:
      operation.startsWith('personal-details') && access.can('employee_fields.personal.read'),
    queryFn: ({ signal }) =>
      getObject<{ employeeId: string; version: number; details: Record<string, unknown> | null }>(
        '/api/v1/employees/{employeeId}/personal-details',
        { employeeId: id },
        signal,
      ),
  });
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const back = () =>
    void navigate({
      to: '/employees/profile/$employeeId',
      params: { employeeId: id },
      search: { tab: kinds.includes(search.kind as EmployeeKind) ? search.kind : undefined },
    });
  const saved = () => {
    toast('Сохранено');
    void client.invalidateQueries();
    back();
  };
  if (employee.isLoading || access.isLoading || personal.isLoading) return <Loading />;
  if (employee.error || !employee.data) return <ErrorState error={employee.error} />;
  const row = employee.data;
  if (operation === 'role-profile-preview')
    return preview.isLoading ? (
      <Loading />
    ) : preview.error ? (
      <ErrorState error={preview.error} />
    ) : (
      <section className="form-page">
        <h1>Профиль должности</h1>
        <StructuredData value={preview.data} />
      </section>
    );
  if (operation === 'role-profile-history')
    return (
      <section className="form-page">
        <h1>История назначений</h1>
        {assignments.isLoading ? (
          <Loading />
        ) : assignments.error ? (
          <ErrorState error={assignments.error} />
        ) : !assignments.rows.length ? (
          <Empty title="Назначений пока нет" />
        ) : (
          assignments.rows.map((assignment) => (
            <section className="assignment-history" key={assignment.id}>
              <h2>
                {new Date(String(assignment.validFrom)).toLocaleDateString('ru-RU')} ·{' '}
                {assignment.validUntil
                  ? new Date(String(assignment.validUntil)).toLocaleDateString('ru-RU')
                  : 'Действует сейчас'}
              </h2>
              <p className="muted">{String(assignment.reason)}</p>
              <StructuredData value={assignment.profile} />
            </section>
          ))
        )}
        {assignments.hasNextPage && (
          <Button onClick={() => void assignments.fetchNextPage()}>Показать ещё</Button>
        )}
      </section>
    );
  if (operation === 'add-document')
    return access.can('employee_fields.documents.write') ? (
      <DocumentUpload employee={row} onClose={back} onSuccess={saved} />
    ) : (
      <Empty title="Недостаточно прав" />
    );
  if (operation === 'personal-details') {
    if (!access.can('employee_fields.personal.read'))
      return <Empty title="Нет доступа к личным данным" />;
    if (personal.error) return <ErrorState error={personal.error} />;
    return (
      <section className="form-page">
        <div className="form-page-heading">
          <h1>Личные данные</h1>
        </div>
        {access.can('employee_fields.personal.write') && (
          <Button
            icon="Pencil"
            onClick={() =>
              void navigate({
                to: '/employees/profile/$employeeId/$operation',
                params: { employeeId: id, operation: 'personal-details-edit' },
              })
            }
          >
            Изменить
          </Button>
        )}
        {personal.data?.details ? (
          <div className="panel details-list">
            {Object.entries(personal.data.details).map(([key, value]) => (
              <div key={key}>
                <span>{fieldLabel(key)}</span>
                <span>{Array.isArray(value) ? value.join(', ') : String(value ?? '—')}</span>
              </div>
            ))}
          </div>
        ) : (
          <Empty title="Личные данные ещё не заполнены" />
        )}
      </section>
    );
  }
  let action: Action | undefined = findResource('people', 'employees')
    ?.actions?.(row)
    .find((item) => actionOperation(item) === operation);
  if (operation === 'edit')
    action = {
      label: 'Рабочие данные',
      method: 'PATCH',
      path: '/api/v1/employees/{employeeId}/work-profile',
      params: { employeeId: id },
      initial: row,
      permission: 'employees.edit',
    };
  if (operation === 'personal-details-edit')
    action = {
      label: 'Личные данные',
      method: 'PATCH',
      path: '/api/v1/employees/{employeeId}/personal-details',
      params: { employeeId: id },
      initial: { ...personal.data?.details, version: personal.data?.version ?? row.version },
      permission: 'employee_fields.personal.write',
    };
  if (operation === 'role-profile')
    action = {
      label: 'Назначить профиль должности',
      path: '/api/v1/employees/{id}/role-profile-assignments',
      params: { id },
      initial: {
        expectedEmployeeVersion: row.version,
        roleProfileVersionId: (resolution.data?.suggestedVersion as { id?: string } | undefined)
          ?.id,
      },
      permission: 'work_plans.manage',
    };
  if (
    operation === 'add-record' &&
    kinds.includes(search.kind as EmployeeKind) &&
    search.kind !== 'documents'
  ) {
    const kind = search.kind!;
    const permission =
      kind === 'compensation' ? 'compensation' : kind === 'payroll' ? 'payroll' : 'screening';
    action = {
      label: 'Добавить запись',
      path: `/api/v1/employees/{employeeId}/${kind}` as GetPath,
      params: { employeeId: id },
      initial: { version: row.version },
      permission: `employee_fields.${permission}.write`,
    };
  }
  if (personal.error) return <ErrorState error={personal.error} />;
  if (operation === 'role-profile' && resolution.isLoading) return <Loading />;
  if (
    operation === 'role-profile' &&
    (resolution.error ||
      !(resolution.data?.allowedActions as string[] | undefined)?.includes('assign'))
  )
    return resolution.error ? (
      <ErrorState error={resolution.error} />
    ) : (
      <Empty title="Назначение профиля недоступно" />
    );
  if (!action) return <Empty title="Страница не найдена" />;
  if (!access.can(action.permission || '', employeeAccessTargets(row, session.account)))
    return <Empty title="Недостаточно прав" />;
  return (
    <ActionForm
      key={operation}
      action={action}
      onClose={back}
      onSuccess={(result: Row) => {
        void result;
        if (operation === 'delete') {
          toast('Сотрудник удалён');
          void client.invalidateQueries();
          void navigate({ to: '/people/employees' });
        } else saved();
      }}
    />
  );
}
